import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useTicketList } from '../hooks/useTicketList'
import { usersAPI } from '../services/api'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  Badge,
  Paper,
  Button,
  useMediaQuery,
  useTheme,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  Autocomplete,
} from '@mui/material'
import {
  Search,
  Refresh,
  ConfirmationNumber as TicketIcon,
  Circle,
  Public as ExternalIcon,
  AutorenewRounded as SpinnerIcon,
  Person as UserIcon,
  SmartToy as AgentIcon,
  Add as AddIcon,
  ExpandMore,
  ExpandLess,
  FilterList,
  Archive as ArchiveIcon,
} from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'
import BotIcon from './BotIcon'

const getStatusColor = (status, theme) => {
  const baseColor = (() => {
    switch (status?.toLowerCase()) {
      case 'new':
        return 'info'
      case 'processing':
        return 'warning'
      case 'waiting for user':
      case 'waiting_for_user':
        return 'success'
      case 'waiting for external':
      case 'waiting_for_external':
      case 'waiting':
        return 'secondary'
      case 'complete':
      case 'completed':
        return 'default'
      case 'error':
      case 'failed':
        return 'error'
      default:
        return 'default'
    }
  })()
  
  // Return muted colors for night mode accessibility
  if (theme?.palette?.mode === 'dark') {
    const mutedColorMap = {
      info: '#42a5f5',
      warning: '#ffb74d',
      success: '#66bb6a',
      error: '#ef5350',
      secondary: '#ba68c8',
      default: '#9e9e9e',
    }
    return mutedColorMap[baseColor] || mutedColorMap.default
  }
  
  return baseColor
}

const getStatusIcon = (status, theme) => {
  const color = getStatusColor(status, theme)
  
  // For night mode, color is already a hex value
  if (theme?.palette?.mode === 'dark' && typeof color === 'string') {
    return (
      <Circle
        sx={{
          fontSize: 12,
          color: color,
        }}
      />
    )
  }
  
  // For light mode, use the color map
  const colorMap = {
    info: '#2196f3',
    warning: '#ff9800',
    secondary: '#9c27b0',
    success: '#4caf50',
    error: '#f44336',
    default: '#757575',
  }
  
  return (
    <Circle
      sx={{
        fontSize: 12,
        color: colorMap[color] || colorMap.default,
      }}
    />
  )
}

const getRoleColor = (role) => {
  switch (role?.toLowerCase()) {
    case 'user':
      return 'primary'
    case 'agent':
      return 'secondary'
    default:
      return 'default'
  }
}

const getRoleIcon = (role) => {
  switch (role?.toLowerCase()) {
    case 'user':
      return <UserIcon sx={{ fontSize: '0.7rem !important' }} />
    case 'agent':
      return <BotIcon sx={{ fontSize: '0.7rem !important' }} />
    default:
      return <UserIcon sx={{ fontSize: '0.7rem !important' }} />
  }
}

const TicketList = ({
  selectedTicketId,
  onTicketSelect,
  createdTicketLoading = false,
  onCreateTicket,
  collapsed = false,
  user = null,
  onRegisterPatch = null,
  onRegisterPrepend = null,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Use the ticket list hook for data management
  const {
    tickets,
    loading,
    error,
    hasMore,
    isLoadingMore,
    loadMore,
    query,
    setQuery,
    filters,
    setFilters,
    refresh,
    patchTicketRow,
    prependTicket,
  } = useTicketList({ selectedTicketId })

  // Register patchTicketRow and prependTicket with the parent so they can be called from outside.
  useEffect(() => {
    onRegisterPatch?.(patchTicketRow)
  }, [onRegisterPatch, patchTicketRow])

  useEffect(() => {
    onRegisterPrepend?.(prependTicket)
  }, [onRegisterPrepend, prependTicket])

  // Ref for infinite scroll intersection observer
  const loadMoreRef = useRef(null)

  // Check if user is admin (from token OR application roles)
  const hasTokenAdminRole = user?.roles?.some(role => role.toLowerCase() === 'agentflow.admin')
  const hasApplicationAdminRole = user?.application_roles?.some(role => role.toLowerCase() === 'application.admin')
  const isAdmin = hasTokenAdminRole || hasApplicationAdminRole

  // Admin user filter state
  const [userOptions, setUserOptions] = useState([])
  const [userSearchLoading, setUserSearchLoading] = useState(false)
  const [userSearchInput, setUserSearchInput] = useState('')
  const userInputFromSelectionRef = useRef(false)

  // Fetch user options when admin types in user filter
  useEffect(() => {
    if (!isAdmin) return
    if (userInputFromSelectionRef.current) return
    const timer = setTimeout(async () => {
      setUserSearchLoading(true)
      try {
        const data = await usersAPI.listUsers({ email_filter: userSearchInput }, 1, 50)
        setUserOptions(data.users || [])
      } catch (e) {
        setUserOptions([])
      } finally {
        setUserSearchLoading(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [userSearchInput, isAdmin])

  // Local UI state for filter panel expansion
  const getStoredUIState = () => {
    try {
      const stored = localStorage.getItem('agentflow-ticket-filters-ui')
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.warn('Failed to parse ticket filters UI from localStorage:', error)
    }
    return {
      filtersExpanded: false
    }
  }

  const saveUIState = (state) => {
    try {
      localStorage.setItem('agentflow-ticket-filters-ui', JSON.stringify(state))
    } catch (error) {
      console.warn('Failed to save ticket filters UI to localStorage:', error)
    }
  }

  const [uiState, setUIState] = useState(() => getStoredUIState())
  const { filtersExpanded } = uiState

  // Use filters from hook
  const {
    showMyTickets = false,
    showAssignedToMe = false,
    ticketType = '',
    ticketStatus = '',
    showArchived = false,
    filterUser = '',
  } = filters || {}

  // For backward compatibility, use query as searchTerm
  const searchTerm = query

  // Update local UI state
  const updateUIState = (key, value) => {
    const newState = { ...uiState, [key]: value }
    setUIState(newState)
    saveUIState(newState)
  }

  // Update filter - now uses hook's setFilters
  const updateServerFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [setFilters])

  // Get unique values for filter dropdowns (from loaded tickets)
  const uniqueTicketTypes = useMemo(() => {
    const types = [...new Set(tickets.map(ticket => ticket.type || ticket.ticket_type).filter(Boolean))]
    return types.sort()
  }, [tickets])

  const uniqueTicketStatuses = useMemo(() => {
    const statuses = [...new Set(tickets.map(ticket => ticket.status).filter(Boolean))]
    return statuses.sort()
  }, [tickets])

  // Track hasMore in a ref to avoid stale closure in intersection observer
  const hasMoreRef = useRef(hasMore)
  const isLoadingMoreRef = useRef(isLoadingMore)

  useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])

  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore
  }, [isLoadingMore])

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Use refs to get current values and avoid stale closures
        if (entries[0].isIntersecting && hasMoreRef.current && !isLoadingMoreRef.current) {
          loadMore()
        }
      },
      { threshold: 0.1 }
    )

    const currentRef = loadMoreRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [loadMore, loading])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      const relativeTime = formatDistanceToNow(date, { addSuffix: true })
      const actualDate = date.toLocaleDateString()
      return `${relativeTime} (${actualDate})`
    } catch {
      return 'Invalid date'
    }
  }

  // Check if any server-side filters are active
  const hasActiveFilters = showMyTickets || showAssignedToMe || ticketType || ticketStatus || showArchived || !!filterUser

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header - Hide when collapsed */}
      {!collapsed && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Search */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <IconButton
                onClick={refresh}
                disabled={loading}
                size="small"
                aria-label="refresh tickets"
              >
                <Refresh />
              </IconButton>
              <Badge
                badgeContent={hasActiveFilters ? '•' : 0}
                color="primary"
                overlap="circular"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '1.2rem',
                    minWidth: 12,
                    height: 12,
                    padding: 0,
                  }
                }}
              >
                <IconButton
                  onClick={() => updateUIState('filtersExpanded', !filtersExpanded)}
                  size="small"
                  aria-label="toggle filters"
                  title="Advanced Filters"
                  sx={{
                    color: hasActiveFilters ? 'primary.main' : 'inherit'
                  }}
                >
                  {filtersExpanded ? <ExpandLess /> : <FilterList />}
                </IconButton>
              </Badge>
            </Box>

            {/* Filters Applied Indicator */}
            {hasActiveFilters && !filtersExpanded && (
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  color: 'primary.main',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <FilterList sx={{ fontSize: '0.9rem' }} />
                Filters Applied
              </Typography>
            )}

            {/* Advanced Filters */}
            <Collapse in={filtersExpanded} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    Advanced Filters
                  </Typography>
                  {hasActiveFilters && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        // Reset all server-side filters
                        setFilters({
                          showMyTickets: false,
                          showAssignedToMe: false,
                          ticketType: '',
                          ticketStatus: '',
                          showArchived: false,
                          filterUser: '',
                        })
                        setUserSearchInput('')
                      }}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </Box>

                <Grid container spacing={2}>
                  {/* My Tickets and Assigned to Me toggles */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {isAdmin ? (
                        <Autocomplete
                          size="small"
                          options={userOptions}
                          loading={userSearchLoading}
                          inputValue={userSearchInput}
                          onInputChange={(_, value, reason) => {
                            if (reason !== 'reset') {
                              userInputFromSelectionRef.current = false
                              setUserSearchInput(value)
                            }
                          }}
                          value={userOptions.find(u => u.email === filterUser) || null}
                          onChange={(_, selected) => {
                            updateServerFilter('filterUser', selected ? selected.email : '')
                            userInputFromSelectionRef.current = !!selected
                            setUserSearchInput(selected ? (selected.name ? `${selected.name} (${selected.email})` : selected.email) : '')
                          }}
                          getOptionLabel={(option) => option.name ? `${option.name} (${option.email})` : option.email}
                          isOptionEqualToValue={(option, value) => option.email === value.email}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Filter by User"
                              placeholder="Search by email..."
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {userSearchLoading ? <CircularProgress size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          sx={{ mb: 1 }}
                        />
                      ) : user?.email && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                          Filtering for: {user.email}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box
                          onClick={() => updateServerFilter('showMyTickets', !showMyTickets)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          <Switch
                            checked={Boolean(showMyTickets)}
                            size="small"
                            color="primary"
                            sx={{ pointerEvents: 'none' }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.875rem',
                              color: 'text.primary',
                              ml: 1
                            }}
                          >
                            My Tickets
                          </Typography>
                        </Box>
                        <Box
                          onClick={() => updateServerFilter('showAssignedToMe', !showAssignedToMe)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          <Switch
                            checked={Boolean(showAssignedToMe)}
                            size="small"
                            color="primary"
                            sx={{ pointerEvents: 'none' }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.875rem',
                              color: 'text.primary',
                              ml: 1
                            }}
                          >
                            Assigned to Me
                          </Typography>
                        </Box>
                        <Box
                          onClick={() => updateServerFilter('showArchived', !showArchived)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          <Switch
                            checked={Boolean(showArchived)}
                            size="small"
                            color="primary"
                            sx={{ pointerEvents: 'none' }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.875rem',
                              color: 'text.primary',
                              ml: 1
                            }}
                          >
                            Archived Only
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Ticket Type and Status dropdowns */}
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Ticket Type</InputLabel>
                      <Select
                        value={ticketType}
                        label="Ticket Type"
                        onChange={(e) => updateServerFilter('ticketType', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>All Types</em>
                        </MenuItem>
                        {uniqueTicketTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={ticketStatus}
                        label="Status"
                        onChange={(e) => updateServerFilter('ticketStatus', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>All Statuses</em>
                        </MenuItem>
                        {uniqueTicketStatuses.map((status) => (
                          <MenuItem key={status} value={status}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getStatusIcon(status, theme)}
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {status}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>

            {/* Add New Ticket Button - Mobile Only */}
            {isMobile && onCreateTicket && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  onClick={onCreateTicket}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: '#ffffff',
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      color: '#ffffff',
                    },
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Collapsed Header - Show only icon actions when collapsed */}
      {collapsed && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <IconButton
            onClick={refresh}
            disabled={loading}
            size="small"
            aria-label="refresh tickets"
            title="Refresh tickets"
          >
            <Refresh />
          </IconButton>
        </Box>
      )}

      {/* Created Ticket Loading Card */}
      {createdTicketLoading && (
        <Box sx={{ px: collapsed ? 1 : 2, pb: 1 }}>
          <ListItem
            sx={{
              py: collapsed ? 1 : 2,
              borderRadius: 2,
              border: theme.palette.mode === 'dark' ? 'none' : '1px solid',
              borderColor: theme.palette.mode === 'dark' ? 'transparent' : 'primary.main',
              backgroundColor: 'primary.light',
              boxShadow: theme.palette.mode === 'dark'
                ? 'var(--agentflow-shadow-elevation)'
                : '0 4px 12px rgba(25, 118, 210, 0.25)',
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            {collapsed ? (
              /* Collapsed view - just spinner */
              <SpinnerIcon
                color="primary"
                fontSize="small"
                sx={{
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': {
                      transform: 'rotate(0deg)',
                    },
                    '100%': {
                      transform: 'rotate(360deg)',
                    },
                  },
                }}
              />
            ) : (
              /* Normal view */
              <>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <SpinnerIcon
                    color="primary"
                    fontSize="small"
                    sx={{
                      animation: 'spin 1s linear infinite',
                      '@keyframes spin': {
                        '0%': {
                          transform: 'rotate(0deg)',
                        },
                        '100%': {
                          transform: 'rotate(360deg)',
                        },
                      },
                    }}
                  />
                </ListItemIcon>

                <ListItemText
                  primary={
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span
                        style={{
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          color: 'white',
                        }}
                      >
                        Creating ticket...
                      </span>
                      <Chip
                        label="processing"
                        size="small"
                        variant="filled"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'primary.contrastText',
                        }}
                      />
                    </span>
                  }
                  secondary={
                    <span
                      style={{
                        fontSize: '0.875rem',
                        color: 'white',
                        opacity: 0.8,
                      }}
                    >
                      Agent will start processing automatically...
                    </span>
                  }
                />
              </>
            )}
          </ListItem>
        </Box>
      )}

      {/* Tickets List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">Loading tickets...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error">Failed to load tickets</Typography>
          </Box>
        ) : tickets.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {searchTerm ? 'No tickets match your search' : 'No tickets found'}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {tickets.map((ticket, index) => (
              <React.Fragment key={ticket.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={selectedTicketId === ticket.id}
                    onClick={() => onTicketSelect(ticket)}
                    sx={{
                      py: 1,
                      px: 2,
                      minHeight: 48,
                      borderRight: theme.palette.mode === 'dark' ? 'none' : '6px solid',
                      borderRightColor: theme.palette.mode === 'dark'
                        ? 'transparent'
                        : (selectedTicketId === ticket.id ? 'primary.main' : 'transparent'),
                      borderBottom: theme.palette.mode === 'dark' ? 'none' : '1px solid',
                      borderBottomColor: theme.palette.mode === 'dark' ? 'transparent' : 'divider',
                      bgcolor: selectedTicketId === ticket.id ? 'primary.50' : 'transparent',
                      boxShadow: theme.palette.mode === 'dark' && selectedTicketId === ticket.id
                        ? 'inset 4px 0 0 0 var(--agentflow-focus-glow), var(--agentflow-shadow-elevation)'
                        : 'none',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' ? 'action.hover' : 'grey.50',
                        borderRightColor: theme.palette.mode === 'dark' ? 'transparent' : 'primary.light',
                        boxShadow: theme.palette.mode === 'dark'
                          ? 'var(--agentflow-shadow-elevation)'
                          : 'none',
                      },
                      '&.Mui-selected': {
                        bgcolor: 'primary.50',
                        '&:hover': {
                          bgcolor: 'primary.100',
                        },
                      },
                      // Enhanced focus states for keyboard navigation
                      '&:focus-visible': {
                        outline: theme.palette.mode === 'dark'
                          ? '2px solid var(--agentflow-focus-glow)'
                          : '2px solid',
                        outlineColor: theme.palette.mode === 'dark'
                          ? 'var(--agentflow-focus-glow)'
                          : 'primary.main',
                        outlineOffset: '2px',
                      },
                    }}
                  >

                    {collapsed ? (
                      /* Collapsed view - show only essential info */
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                        py: 1
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                          #{ticket.id}
                        </Typography>
                        {getStatusIcon(ticket.status === 'waiting' ? `waiting for ${ticket.role}` : ticket.status, theme)}
                      </Box>
                    ) : (
                      /* Normal view */
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                #{ticket.id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 300 }}>
                                {ticket.type || ticket.ticket_type || 'Unknown Type'}
                              </Typography>
                              {ticket.archived && (
                                <Chip
                                  icon={<ArchiveIcon sx={{ fontSize: '0.7rem !important' }} />}
                                  label="ARCHIVED"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    backgroundColor: 'grey.300',
                                    color: 'grey.700',
                                    '& .MuiChip-icon': {
                                      marginLeft: '4px',
                                    },
                                  }}
                                />
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {getStatusIcon(ticket.status === 'waiting' ? `waiting for ${ticket.role}` : ticket.status, theme)}
                              <Typography variant="caption" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                                {ticket.status === 'waiting' ? `waiting for ${ticket.role}` : ticket.status}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Box>
                            {ticket.data && Object.keys(ticket.data).length > 0 && (() => {
                              const firstStringEntry = Object.entries(ticket.data).find(([key, value]) => typeof value === 'string');
                              return firstStringEntry ? (
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                                  {`${firstStringEntry[0].replace(/_/g, ' ')}: ${firstStringEntry[1]}`}
                                </Typography>
                              ) : null;
                            })()}
                            {ticket.created_at && (
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', display: 'block', mt: 0.25 }}>
                                {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                              </Typography>
                            )}
                          </Box>
                        }
                        secondaryTypographyProps={{
                          component: "div",
                          sx: { mt: 0.5 }
                        }}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}

            {/* Infinite scroll sentinel and loading indicator */}
            {loadMore && (
              <Box
                ref={loadMoreRef}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 2,
                  minHeight: 48
                }}
              >
                {isLoadingMore ? (
                  <CircularProgress size={24} />
                ) : hasMore ? (
                  <Typography variant="caption" color="text.secondary">
                    Scroll for more...
                  </Typography>
                ) : tickets.length > 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No more tickets
                  </Typography>
                ) : null}
              </Box>
            )}
          </List>
        )}
      </Box>
    </Box>
  )
}

export default TicketList